<template>
  <section class="student-reviews">
    <div class="home-padding container-xxl">
      <div class="header-background">
        <h2 class="reviews-title">Hear from our students</h2>
      </div>
    </div>

    <div class="reviews-slider-container">
      <swiper
        :modules="[SwiperPagination, SwiperNavigation, SwiperAutoplay]"
        :slides-per-view="1.2"
        :space-between="16"
        :centered-slides="true"
        :loop="true"
        :loopedSlides="6"
        :initial-slide="2"
        :autoplay="false"
        :grab-cursor="true"
        :breakpoints="{
          768: {
            slidesPerView: 2,
            spaceBetween: 4,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 32,
          },
        }"
        :effect="'slide'"
        :centered-slides-bounds="true"
        :slides-offset-before="0"
        :slides-offset-after="0"
        :navigation="{
          nextEl: '.custom-button-next',
          prevEl: '.custom-button-prev',
        }"
        class="reviews-swiper"
        @swiper="onSwiperInit"
      >
        <swiper-slide
          v-for="review in reviews"
          :key="review.id"
          class="review-slide"
        >
          <div class="review-card">
            <div class="quote-icon" aria-hidden="true">
              <img src="/images/quote-icon.svg" alt="quote icon" />
            </div>
            <p class="review-text">{{ review.text }}</p>
            <div class="reviewer-info">
              <div class="reviewer-details">
                <span class="reviewer-name"
                  >{{ review.name }}, {{ review.grade }}</span
                >
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper>

      <div class="custom-navigation">
        <div class="nav-buttons-container">
          <button class="custom-button-prev" aria-label="Previous slide">
            <svg
              width="9"
              height="16"
              viewBox="0 0 9 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 15L1 8L8 1"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button class="custom-button-next" aria-label="Next slide">
            <svg
              width="9"
              height="16"
              viewBox="0 0 9 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.999999 1L8 8L1 15"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
// Import Swiper Vue components
import { Swiper, SwiperSlide } from "swiper/vue";

// Import Swiper modules
import { Pagination, Navigation, Autoplay } from "swiper/modules";

// Import Swiper styles (ensure these paths are correct in your project)
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Assign modules to variables Vue can use in the template
const SwiperPagination = Pagination;
const SwiperNavigation = Navigation;
const SwiperAutoplay = Autoplay;

// Swiper instance ref
const swiperInstance = ref(null);

// Callback function when Swiper initializes
const onSwiperInit = (swiper) => {
  swiperInstance.value = swiper;
  // Force update and center the slide on initialization
  setTimeout(() => {
    swiper.update();
    swiper.slideToLoop(2, 0); // Center on the middle slide (index 2)
  }, 100);
};

// Sample review data - duplicated to ensure smooth loop behavior
const reviews = [
  {
    id: 1,
    text: "The physical making session taught me that it’s okay to fail on your first try. My first prototype didn't work at all, but the mentors helped me figure out why, and my second version was awesome!",
    name: "Leo",
    grade: "5th Grade",
  },
  {
    id: 2,
    text: "I’ve always played games, but I never thought I’d be able to make one. The digital making tools were so easy to use once I got started, and seeing my own code come to life was such a rush.",
    name: "Sofia",
    grade: "7th Grade",
  },
  {
    id: 3,
    text: "It was really cool to see how everyone used the same materials to make completely different things. It showed me there isn't just one right way to solve a problem.",
    name: "Ethan",
    grade: "4th Grade",
  },
  {
    id: 4,
    text: "The physical making session taught me that it's okay to fail on your first try. My first prototype didn't work at all, but the mentors helped me figure out why, and my second version was awesome!",
    name: "Leo",
    grade: "5th Grade",
  },
  {
    id: 6,
    text: "I've always played games, but I never thought I'd be able to make one. The digital making tools were so easy to use once I got started, and seeing my own code come to life was such a rush.",
    name: "Sofia",
    grade: "7th Grade",
  },
  {
    id: 5,
    text: "TIt was really cool to see how everyone used the same materials to make completely different things. It showed me there isn't just one right way to solve a problem.",
    name: "Ethan",
    grade: "4th Grade",
  },
];

// Keyboard navigation (remains the same)
onMounted(() => {
  document.addEventListener("keydown", (e) => {
    if (!swiperInstance.value) return;
    if (e.key === "ArrowLeft") {
      swiperInstance.value.slidePrev();
    } else if (e.key === "ArrowRight") {
      swiperInstance.value.slideNext();
    }
  });
});
</script>

<style>
/* Global Swiper overrides - Target the specific swiper instance if needed */
.reviews-swiper .swiper-pagination-bullet {
  width: 10px;
  height: 10px;
  background-color: #ddd;
  /* Example color */
  opacity: 1;
  transition: all 0.3s ease;
  margin: 0 5px !important;
  /* Add horizontal margin */
}

.reviews-swiper .swiper-pagination-bullet-active {
  background-color: var(--primary-blue);
  /* Example active color */
  transform: scale(1.2);
}

/* Ensure pagination container allows bullets to display inline */
.swiper-pagination-dots {
  position: relative;
  /* Or static, depending on layout */
  bottom: auto;
  /* Reset default positioning */
  left: auto;
  width: 100%;
  text-align: center;
  margin-bottom: 15px;
  /* Space below dots */
}

/* --- Custom Navigation Buttons --- */
.custom-button-prev,
.custom-button-next {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background-color: var(--primary-blue, #003d6a);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
}

.custom-button-prev:hover,
.custom-button-next:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.custom-button-prev:active,
.custom-button-next:active {
  transform: translateY(0);
}

/* --- Slide Opacity/Transform for non-active (works with coverflow) --- */
@media (min-width: 769px) {
  .reviews-swiper .swiper-slide {
    opacity: 1;
    transform: scale(0.836);
    transition:
      opacity 0.5s ease,
      transform 0.5s ease;
  }

  .reviews-swiper .swiper-slide-prev {
    transform-origin: right center;
  }

  .reviews-swiper .swiper-slide-next {
    transform-origin: left center;
  }

  .reviews-swiper .swiper-slide-active {
    opacity: 1;
    transform: scale(1);
    z-index: 2;
  }
}

/* --- Hide default navigation buttons if using custom ones --- */
/* This might not be needed if not rendered, but good practice */
.reviews-swiper > .swiper-button-next,
.reviews-swiper > .swiper-button-prev {
  display: none !important;
}
</style>

<style scoped>
/* Scoped component styles remain largely the same */
.swiper {
  /* Hide adjacent slides outside the view */
  overflow: hidden !important;
}

.reviews-swiper {
  /* Add padding inside swiper so box-shadow rendering doesn't get clipped by hidden overflow */
  padding: 25px;
}

.student-reviews {
  padding: 3rem 0;
  max-width: 100%;
  margin: 0 auto;
  font-family:
    "League Spartan",
    -apple-system,
    sans-serif;
  overflow-x: hidden;
  /* Prevent horizontal scroll on the main section */
}

.header-background {
  background: var(--white-hover-1);
  backdrop-filter: blur(4px);
  padding: 10px;
  border-radius: 16px;
}

.reviews-title {
  font-size: 36px;
  color: var(--primary-blue);
  text-align: center;
  margin: 0.5rem 0;
}

.reviews-slider-container {
  position: relative;
  max-width: 1440px; /* Align with Figma design */
  margin: 20px auto 0 auto;
}

.review-slide {
  height: auto;
  /* Let content determine height */
  display: flex;
  justify-content: center;
  align-items: center;
  /* Center card vertically if slide is taller */
  /* Transition is handled globally now */
  /* Add bottom padding if navigation overlaps */
  padding-bottom: 20px;
  max-width: 100%;
  /* Allow swiper to control width */
}

.review-card {
  background-color: #fff;
  border-radius: 16px;
  padding: 25px;
  width: 100%;
  min-height: 250px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  transition:
    box-shadow 0.5s ease,
    transform 0.5s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

@media (min-width: 769px) {
  .review-card {
    border-radius: 19px;
    padding: 19px;
    max-width: 402px;
    margin: 0 auto;
    min-height: 0;
    gap: 19px;
    box-shadow:
      0 4px 8px 3px rgba(0, 0, 0, 0.15),
      0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }
}

/* Active slide card gets more prominent shadow */
.swiper-slide-active .review-card {
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  /* Transform is handled by slide styles now */
}

.quote-icon {
  margin-bottom: 15px;
}

.quote-icon img {
  width: 60px;
  height: 48px;
}

.review-text {
  font-size: var(--fs-large);
  line-height: 1.5;
  color: var(--primary-blue);
  margin-bottom: 25px;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  margin: 0;
}

@media (min-width: 769px) {
  .quote-icon {
    margin-bottom: 0;
  }

  .quote-icon img {
    width: 80px;
    height: 86px;
  }

  .review-text {
    margin-top: 0;
    margin-bottom: 0;
    font-size: 26px;
    line-height: 33px;
    -webkit-line-clamp: unset;
    line-clamp: unset;
    overflow: visible;
    text-overflow: clip;
  }
}

.reviewer-info {
  display: flex;
  align-items: center;
  margin-top: auto;
  /* Push to bottom */
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1rem;
  flex-shrink: 0;
  /* Prevent avatar shrinking */
}

.reviewer-details {
  display: flex;
  flex-direction: column;
  text-align: left;
  /* Ensure text aligns left */
}

.reviewer-name {
  font-weight: 600;
  color: var(--primary-blue);
  font-size: var(--fs-big);
}

@media (min-width: 769px) {
  .reviewer-name {
    font-weight: 700;
    font-size: 24px;
    line-height: 33px;
    letter-spacing: 0.036px;
  }
}

.reviewer-grade {
  font-size: 0.85rem;
  color: #666;
}

/* Custom navigation container at bottom */
.custom-navigation {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;
  /* Increased top margin */
  width: 100%;
  position: relative;
  /* Needed for z-index if overlap occurs */
  z-index: 10;
}

.nav-buttons-container {
  display: flex;
  justify-content: center;
  gap: 20px;
  /* Space between buttons */
  margin-top: 15px;
  /* Space above buttons */
}

/* Mobile carousel styles */
@media (max-width: 768px) {
  .reviews-swiper {
    overflow: visible !important;
    padding: 0 10px;
  }

  .student-reviews {
    padding: 0;
  }

  .reviews-slider-container {
    margin: 40px 0px;
  }

  .review-slide {
    opacity: 1;
    transform: scale(0.95);
    transition:
      opacity 0.5s ease,
      transform 0.5s ease;
    display: flex;
    justify-content: center;
  }

  .reviews-swiper .swiper-slide-active {
    opacity: 1;
    transform: scale(1);
  }

  .reviews-swiper .review-card {
    width: 100%;
    max-width: 321px;
    margin: 0 auto;
    opacity: 1 !important;
    animation: none !important;
  }

  .review-card {
    height: auto;
    min-height: 360px;
    border-radius: 16px;
    padding: 16px;
    gap: 16px;
    box-shadow:
      0 4px 8px 3px rgba(0, 0, 0, 0.15),
      0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .quote-icon {
    margin-bottom: 0;
  }

  .quote-icon img {
    width: 67.5px;
    height: 72px;
  }

  .review-text {
    font-size: 22px;
    line-height: 28px;
    margin-bottom: 0;
    -webkit-line-clamp: unset;
  }

  .reviewer-name {
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.03px;
  }

  .header-background {
    background: var(--white-hover-1);
  }

  .custom-navigation {
    display: none;
  }
}

@media (max-width: 640px) {
  .reviews-title {
    font-size: 1.5rem;
  }
}
</style>
